// The only door to data (docs/frontend/PLAN.md section 6). Pages use the hooks in ./hooks, never this file.
// The sample client runs in the browser over @offsilo/sample-data with realistic latency and optimistic writes.
// The server client will implement the same interface.
import type { Workspace } from "@offsilo/sample-data";
import { createServerClient } from "./server-client";

export type CollectionName = Exclude<keyof Workspace, "workspace" | "metrics">;
export type Row<C extends CollectionName> = Workspace[C][number];

/** A patch may clear an optional field: null removes the value on the server (and in the cache, see hooks merge). */
export type Patch<C extends CollectionName> = { collection: C; id: string; changes: { [K in keyof Row<C>]?: Row<C>[K] | null } };

export interface DataClient {
  workspace(): Promise<Workspace["workspace"]>;
  /** `week` moves the Time card's week: "last" and "next" are the weeks either side of this one. */
  metrics(week?: "this" | "last" | "next"): Promise<Workspace["metrics"]>;
  /** chrome: shell data (sidebar, rail), which ignores the forced page states. */
  /** fg: a filter tree the server applies (base64url JSON, X13-0101a); the sample data ignores it and the page filters locally. */
  /** archived (notifications only): the archived rows instead of the inbox (X21-OPS-24). */
  list<C extends CollectionName>(collection: C, opts?: { chrome?: boolean; fg?: string; archived?: boolean }): Promise<Row<C>[]>;
  get<C extends CollectionName>(collection: C, id: string): Promise<Row<C> | undefined>;
  /** Applies a patch and returns the inverse patch, so callers can offer Undo. */
  update<C extends CollectionName>(patch: Patch<C>): Promise<Patch<C>>;
  /** Adds a row (an id is generated when missing) and returns it. */
  create<C extends CollectionName>(collection: C, row: Omit<Row<C>, "id"> & { id?: string }): Promise<Row<C>>;
  /** Removes a row and returns it, so callers can offer Undo by creating it again. */
  remove<C extends CollectionName>(collection: C, id: string): Promise<Row<C>>;
}

/** Forced page states for checks and review: ?state=loading|empty|error|partial (PLAN.md section 6). */
export type ForcedState = "ready" | "loading" | "empty" | "error" | "partial";
export function forcedState(): ForcedState {
  if (typeof location === "undefined") return "ready";
  const s = new URLSearchParams(location.search).get("state");
  return s === "loading" || s === "empty" || s === "error" || s === "partial" ? s : "ready";
}

/** The sample client, loaded on its first call: the sample data is a chunk of its own and a server build never fetches it. */
function lazySampleClient(): DataClient {
  let loaded: Promise<DataClient> | undefined;
  const sample = () => (loaded ??= import("./sample-client").then((m) => m.createSampleClient()));
  const call = (name: keyof DataClient) => (...args: unknown[]) =>
    sample().then((c) => (c[name] as (...a: unknown[]) => Promise<unknown>).apply(c, args));
  return {
    workspace: call("workspace"),
    metrics: call("metrics"),
    list: call("list"),
    get: call("get"),
    update: call("update"),
    create: call("create"),
    remove: call("remove"),
  } as unknown as DataClient;
}

// VITE_DATA=server talks to the API (apps/server); anything else keeps the in-browser sample data.
export const client: DataClient = import.meta.env.VITE_DATA === "server" ? createServerClient() : lazySampleClient();
